import React, { useState } from 'react';
import { useProject } from '../../contexts/ProjectContext';
import { Action } from '../../lib/auth';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import {
  Sparkles,
  Calendar,
  Clock,
  ListTree,
  Activity,
  ArrowRight,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Building,
  Wrench,
  Factory,
  Home,
  Store,
  Hammer,
  HardHat,
  ChevronRight,
  ChevronLeft,
  X,
  Building2,
  Warehouse,
  TreePine,
  Truck,
  Droplets,
  Zap,
  Hotel,
  School,
  Hospital,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, addMonths } from 'date-fns';
import { Calendar as CalendarComponent } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { cn } from '../../lib/utils';

// Project Types with their corresponding Sub-Types
const PROJECT_TYPE_CONFIG = {
  residential: {
    label: 'Residential',
    icon: Home,
    description: 'Housing, apartments, condos',
    subTypes: [
      { value: 'single_family', label: 'Single-Family Home', description: 'Detached houses, custom homes' },
      { value: 'low_rise', label: 'Low-Rise (1-4 stories)', description: 'Garden apartments, townhomes' },
      { value: 'mid_rise', label: 'Mid-Rise (5-12 stories)', description: 'Apartment buildings, condos' },
      { value: 'high_rise', label: 'High-Rise (13+ stories)', description: 'Towers, luxury condos' },
      { value: 'mixed_use_residential', label: 'Mixed-Use Residential', description: 'Residential with retail/office' },
    ]
  },
  commercial: {
    label: 'Commercial',
    icon: Store,
    description: 'Retail, malls, restaurants',
    subTypes: [
      { value: 'retail_standalone', label: 'Standalone Retail', description: 'Stores, restaurants, banks' },
      { value: 'shopping_center', label: 'Shopping Center', description: 'Strip malls, plazas' },
      { value: 'mall', label: 'Shopping Mall', description: 'Enclosed malls, outlets' },
      { value: 'hotel', label: 'Hotel / Hospitality', description: 'Hotels, resorts, motels' },
      { value: 'restaurant', label: 'Restaurant / Food Service', description: 'Restaurants, cafes, bars' },
    ]
  },
  building: {
    label: 'Office Building',
    icon: Building,
    description: 'Office buildings, towers, structures',
    subTypes: [
      { value: 'office_low_rise', label: 'Low-Rise Office', description: '1-4 story office buildings' },
      { value: 'office_mid_rise', label: 'Mid-Rise Office', description: '5-12 story office buildings' },
      { value: 'office_high_rise', label: 'High-Rise Office', description: '13+ story office towers' },
      { value: 'office_campus', label: 'Office Campus', description: 'Multi-building office parks' },
      { value: 'medical_office', label: 'Medical Office Building', description: 'Clinics, medical centers' },
    ]
  },
  infrastructure: {
    label: 'Infrastructure',
    icon: HardHat,
    description: 'Roads, bridges, utilities',
    subTypes: [
      { value: 'roadway', label: 'Roadway / Highway', description: 'Roads, highways, interchanges' },
      { value: 'bridge', label: 'Bridge / Structure', description: 'Bridges, overpasses, tunnels' },
      { value: 'water_sewer', label: 'Water / Sewer', description: 'Pipelines, treatment plants' },
      { value: 'electrical', label: 'Electrical / Power', description: 'Substations, power lines' },
      { value: 'transit', label: 'Transit / Rail', description: 'Rail, stations, transit systems' },
    ]
  },
  industrial: {
    label: 'Industrial',
    icon: Factory,
    description: 'Factories, plants, warehouses',
    subTypes: [
      { value: 'manufacturing', label: 'Manufacturing Plant', description: 'Factories, production facilities' },
      { value: 'warehouse', label: 'Warehouse / Distribution', description: 'Distribution centers, logistics' },
      { value: 'data_center', label: 'Data Center', description: 'Server farms, tech facilities' },
      { value: 'processing', label: 'Processing Plant', description: 'Food, chemical, material processing' },
      { value: 'cold_storage', label: 'Cold Storage', description: 'Refrigerated warehouses' },
    ]
  },
  institutional: {
    label: 'Institutional',
    icon: School,
    description: 'Schools, hospitals, government',
    subTypes: [
      { value: 'education_k12', label: 'K-12 School', description: 'Elementary, middle, high schools' },
      { value: 'higher_education', label: 'Higher Education', description: 'Universities, colleges' },
      { value: 'healthcare', label: 'Healthcare Facility', description: 'Hospitals, clinics, labs' },
      { value: 'government', label: 'Government Building', description: 'Courthouses, civic centers' },
      { value: 'religious', label: 'Religious / Assembly', description: 'Churches, mosques, temples' },
    ]
  },
  renovation: {
    label: 'Renovation',
    icon: Hammer,
    description: 'Remodel, retrofit, upgrades',
    subTypes: [
      { value: 'interior_renovation', label: 'Interior Renovation', description: 'Tenant improvements, remodels' },
      { value: 'facade_renovation', label: 'Facade / Envelope', description: 'Building envelope, windows' },
      { value: 'seismic_retrofit', label: 'Seismic Retrofit', description: 'Structural upgrades' },
      { value: 'historic', label: 'Historic Restoration', description: 'Historic building restoration' },
      { value: 'system_upgrade', label: 'Systems Upgrade', description: 'MEP, HVAC, electrical upgrades' },
    ]
  },
  civil: {
    label: 'Civil / Site',
    icon: Wrench,
    description: 'Site work, grading, earthwork',
    subTypes: [
      { value: 'site_development', label: 'Site Development', description: 'Grading, utilities, paving' },
      { value: 'earthwork', label: 'Earthwork', description: 'Mass excavation, grading' },
      { value: 'landscaping', label: 'Landscaping', description: 'Parks, plazas, hardscape' },
      { value: 'parking', label: 'Parking Structure', description: 'Garages, surface lots' },
      { value: 'stormwater', label: 'Stormwater Management', description: 'Detention, drainage systems' },
    ]
  },
};

// Flatten for backward compatibility
const PROJECT_TYPES = Object.entries(PROJECT_TYPE_CONFIG).map(([value, config]) => ({
  value,
  label: config.label,
  icon: config.icon,
  description: config.description,
}));

const DETAIL_LEVELS = [
  { value: 'high_level', label: 'High Level', activities: '10-30', description: 'Summary schedule with major milestones' },
  { value: 'moderate', label: 'Moderate', activities: '30-60', description: 'Standard detail for typical projects' },
  { value: 'detailed', label: 'Detailed', activities: '60-100+', description: 'Comprehensive breakdown of all tasks' },
];

const AIScheduleDialog = ({ onScheduleCreated }) => {
  const { activeProject, can } = useProject();
  
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1); // 1: Input, 2: Preview, 3: Creating
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    schedule_name: '',
    project_type: '',
    project_subtype: '',
    scope_description: '',
    major_phases: '',
    start_date: new Date(),
    target_end_date: null,
    duration_months: 12,
    working_days_per_week: 5,
    hours_per_day: 8,
    detail_level: 'moderate',
  });

  const resetDialog = () => {
    setStep(1);
    setPreview(null);
    setError(null);
    setFormData({
      schedule_name: '',
      project_type: '',
      project_subtype: '',
      scope_description: '',
      major_phases: '',
      start_date: new Date(),
      target_end_date: null,
      duration_months: 12,
      working_days_per_week: 5,
      hours_per_day: 8,
      detail_level: 'moderate',
    });
  };

  // Get available sub-types based on selected project type
  const availableSubTypes = formData.project_type 
    ? PROJECT_TYPE_CONFIG[formData.project_type]?.subTypes || []
    : [];

  // Handle project type change - reset subtype when type changes
  const handleProjectTypeChange = (typeValue) => {
    setFormData({ 
      ...formData, 
      project_type: typeValue,
      project_subtype: '' // Reset subtype when type changes
    });
  };

  const handleOpenChange = (isOpen) => {
    setOpen(isOpen);
    if (!isOpen) {
      resetDialog();
    }
  };

  const validateForm = () => {
    if (!formData.schedule_name.trim()) {
      toast.error('Please enter a schedule name');
      return false;
    }
    if (!formData.project_type) {
      toast.error('Please select a project type');
      return false;
    }
    if (!formData.project_subtype) {
      toast.error('Please select a project sub-type');
      return false;
    }
    if (!formData.scope_description.trim() || formData.scope_description.length < 10) {
      toast.error('Please provide a detailed scope description (at least 10 characters)');
      return false;
    }
    if (!formData.start_date) {
      toast.error('Please select a start date');
      return false;
    }
    return true;
  };

  const handleGenerate = async () => {
    if (!validateForm()) return;
    
    setIsGenerating(true);
    setError(null);
    
    try {
      // Get the sub-type label for more context in the AI prompt
      const subTypeInfo = availableSubTypes.find(st => st.value === formData.project_subtype);
      
      const payload = {
        project_id: activeProject.id,
        schedule_name: formData.schedule_name,
        project_type: formData.project_type,
        project_subtype: formData.project_subtype,
        project_subtype_label: subTypeInfo?.label || '',
        project_subtype_description: subTypeInfo?.description || '',
        scope_description: formData.scope_description,
        major_phases: formData.major_phases || null,
        start_date: format(formData.start_date, 'yyyy-MM-dd'),
        target_end_date: formData.target_end_date ? format(formData.target_end_date, 'yyyy-MM-dd') : null,
        duration_months: formData.target_end_date ? null : formData.duration_months,
        working_days_per_week: formData.working_days_per_week,
        hours_per_day: formData.hours_per_day,
        detail_level: formData.detail_level,
      };
      
      const response = await api.post('/ai-schedule/generate', payload);
      setPreview(response.data);
      setStep(2);
      toast.success('Schedule preview generated!');
    } catch (err) {
      console.error('Failed to generate schedule:', err);
      setError(err.response?.data?.detail || 'Failed to generate schedule. Please try again.');
      toast.error('Failed to generate schedule');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCreate = async () => {
    if (!preview) return;
    
    setIsCreating(true);
    setStep(3);
    
    try {
      const response = await api.post('/ai-schedule/create', {
        project_id: activeProject.id,
        preview: preview,
      });
      
      toast.success(response.data.message || 'Schedule created successfully!');
      setOpen(false);
      resetDialog();
      
      if (onScheduleCreated) {
        onScheduleCreated(response.data);
      }
    } catch (err) {
      console.error('Failed to create schedule:', err);
      toast.error(err.response?.data?.detail || 'Failed to create schedule');
      setStep(2); // Go back to preview
    } finally {
      setIsCreating(false);
    }
  };

  if (!activeProject || !can(Action.MANAGE_SCHEDULES)) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2" data-testid="ai-schedule-btn">
          <Sparkles className="h-4 w-4 text-purple-500" />
          Create with AI
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[700px] h-[85vh] max-h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-500" />
            {step === 1 && 'AI-Assisted Schedule Creation'}
            {step === 2 && 'Review Generated Schedule'}
            {step === 3 && 'Creating Schedule...'}
          </DialogTitle>
          <DialogDescription>
            {step === 1 && 'Describe your project and let AI generate a complete schedule with WBS, activities, and logic.'}
            {step === 2 && 'Review the AI-generated schedule before creating it.'}
            {step === 3 && 'Please wait while we create your schedule...'}
          </DialogDescription>
        </DialogHeader>

        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-2 py-2 flex-shrink-0">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                step >= s 
                  ? 'bg-purple-500 text-white' 
                  : 'bg-slate-100 text-slate-400'
              }`}>
                {step > s ? <CheckCircle2 className="h-4 w-4" /> : s}
              </div>
              {s < 3 && <ChevronRight className={`h-4 w-4 ${step > s ? 'text-purple-500' : 'text-slate-300'}`} />}
            </React.Fragment>
          ))}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto pr-2">
          {/* Step 1: Input Form */}
          {step === 1 && (
            <div className="space-y-4 py-2">
              {/* Schedule Name */}
              <div className="space-y-2">
                <Label htmlFor="schedule-name">Schedule Name *</Label>
                <Input
                  id="schedule-name"
                  placeholder="e.g., Main Building Schedule Rev 1"
                  value={formData.schedule_name}
                  onChange={(e) => setFormData({ ...formData, schedule_name: e.target.value })}
                />
              </div>

              {/* Project Type */}
              <div className="space-y-2">
                <Label>Project Type *</Label>
                <div className="grid grid-cols-4 gap-2">
                  {PROJECT_TYPES.map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.value}
                        type="button"
                        data-testid={`project-type-${type.value}`}
                        onClick={() => handleProjectTypeChange(type.value)}
                        className={`p-3 rounded-lg border text-center transition-all ${
                          formData.project_type === type.value
                            ? 'bg-purple-50 border-purple-300 text-purple-700 ring-2 ring-purple-200'
                            : 'bg-white border-slate-200 hover:bg-slate-50 hover:border-slate-300'
                        }`}
                      >
                        <Icon className={`h-5 w-5 mx-auto mb-1 ${
                          formData.project_type === type.value ? 'text-purple-500' : 'text-slate-400'
                        }`} />
                        <span className="text-xs font-medium">{type.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Project Sub-Type (Dynamic based on Project Type) */}
              {formData.project_type && availableSubTypes.length > 0 && (
                <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
                  <Label className="flex items-center gap-2">
                    <ChevronRight className="h-4 w-4 text-purple-500" />
                    Sub-Type *
                    <Badge variant="outline" className="text-[10px] ml-1">
                      {PROJECT_TYPE_CONFIG[formData.project_type]?.label}
                    </Badge>
                  </Label>
                  <div className="grid grid-cols-2 gap-2">
                    {availableSubTypes.map((subType) => (
                      <button
                        key={subType.value}
                        type="button"
                        data-testid={`project-subtype-${subType.value}`}
                        onClick={() => setFormData({ ...formData, project_subtype: subType.value })}
                        className={`p-3 rounded-lg border text-left transition-all ${
                          formData.project_subtype === subType.value
                            ? 'bg-purple-50 border-purple-300 ring-2 ring-purple-200'
                            : 'bg-white border-slate-200 hover:bg-slate-50 hover:border-slate-300'
                        }`}
                      >
                        <span className={`text-sm font-medium block ${
                          formData.project_subtype === subType.value ? 'text-purple-700' : 'text-slate-700'
                        }`}>
                          {subType.label}
                        </span>
                        <span className="text-[11px] text-slate-500 line-clamp-1">{subType.description}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Scope Description */}
              <div className="space-y-2">
                <Label htmlFor="scope">Scope Description *</Label>
                <Textarea
                  id="scope"
                  placeholder="Describe the project scope, including size, key features, and any special requirements...&#10;&#10;Example: 20-story commercial office tower with underground parking, retail podium on levels 1-3, typical office floors 4-20, rooftop mechanical penthouse, and landscaped plaza."
                  value={formData.scope_description}
                  onChange={(e) => setFormData({ ...formData, scope_description: e.target.value })}
                  className="min-h-[100px]"
                />
                <p className="text-xs text-slate-500">More detail = better schedule quality</p>
              </div>

              {/* Major Phases (Optional) */}
              <div className="space-y-2">
                <Label htmlFor="phases">Major Phases (Optional)</Label>
                <Textarea
                  id="phases"
                  placeholder="List specific phases if you have requirements...&#10;e.g., Demolition, Excavation, Foundation, Podium Structure, Tower Structure, MEP Rough-in, Enclosure, Finishes"
                  value={formData.major_phases}
                  onChange={(e) => setFormData({ ...formData, major_phases: e.target.value })}
                  className="min-h-[60px]"
                />
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <Calendar className="mr-2 h-4 w-4" />
                        {formData.start_date ? format(formData.start_date, 'PPP') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={formData.start_date}
                        onSelect={(date) => setFormData({ ...formData, start_date: date })}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>Duration (Months)</Label>
                  <Select
                    value={String(formData.duration_months)}
                    onValueChange={(value) => setFormData({ ...formData, duration_months: parseInt(value) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[3, 6, 9, 12, 18, 24, 36, 48, 60].map((m) => (
                        <SelectItem key={m} value={String(m)}>{m} months</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Work Schedule */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Working Days/Week</Label>
                  <Select
                    value={String(formData.working_days_per_week)}
                    onValueChange={(value) => setFormData({ ...formData, working_days_per_week: parseInt(value) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5 days (Mon-Fri)</SelectItem>
                      <SelectItem value="6">6 days (Mon-Sat)</SelectItem>
                      <SelectItem value="7">7 days (All week)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Hours/Day</Label>
                  <Select
                    value={String(formData.hours_per_day)}
                    onValueChange={(value) => setFormData({ ...formData, hours_per_day: parseInt(value) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="8">8 hours</SelectItem>
                      <SelectItem value="10">10 hours</SelectItem>
                      <SelectItem value="12">12 hours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Detail Level */}
              <div className="space-y-2">
                <Label>Schedule Detail Level</Label>
                <div className="grid grid-cols-3 gap-2">
                  {DETAIL_LEVELS.map((level) => (
                    <button
                      key={level.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, detail_level: level.value })}
                      className={`p-3 rounded-lg border text-left transition-colors ${
                        formData.detail_level === level.value
                          ? 'bg-purple-50 border-purple-300'
                          : 'bg-white border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      <span className={`text-sm font-medium block ${
                        formData.detail_level === level.value ? 'text-purple-700' : 'text-slate-700'
                      }`}>{level.label}</span>
                      <span className="text-[10px] text-slate-500">{level.activities} activities</span>
                    </button>
                  ))}
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="h-4 w-4 text-red-500 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Preview */}
          {step === 2 && preview && (
            <div className="space-y-4 py-2">
              {/* Summary Cards */}
              <div className="grid grid-cols-4 gap-3">
                <Card className="bg-purple-50 border-purple-200">
                  <CardContent className="p-3">
                    <div className="text-[10px] uppercase text-purple-600 mb-1">WBS Elements</div>
                    <div className="text-xl font-bold text-purple-800">{preview.total_wbs_count}</div>
                  </CardContent>
                </Card>
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-3">
                    <div className="text-[10px] uppercase text-blue-600 mb-1">Activities</div>
                    <div className="text-xl font-bold text-blue-800">{preview.total_activity_count}</div>
                  </CardContent>
                </Card>
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-3">
                    <div className="text-[10px] uppercase text-green-600 mb-1">Relationships</div>
                    <div className="text-xl font-bold text-green-800">{preview.total_relationships}</div>
                  </CardContent>
                </Card>
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="p-3">
                    <div className="text-[10px] uppercase text-amber-600 mb-1">Duration</div>
                    <div className="text-xl font-bold text-amber-800">{preview.total_duration_days}d</div>
                  </CardContent>
                </Card>
              </div>

              {/* Timeline */}
              <Card>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xs text-slate-500">Start</span>
                      <p className="text-sm font-medium">{format(new Date(preview.start_date), 'MMM d, yyyy')}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-slate-400" />
                    <div className="text-right">
                      <span className="text-xs text-slate-500">Estimated End</span>
                      <p className="text-sm font-medium">{format(new Date(preview.estimated_end_date), 'MMM d, yyyy')}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Summary */}
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">{preview.summary}</p>
              </div>

              {/* WBS & Activities Preview */}
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <ListTree className="h-4 w-4" />
                  Work Breakdown Structure
                </h4>
                <Accordion type="multiple" className="border rounded-lg">
                  {preview.wbs_elements.map((wbs, index) => (
                    <AccordionItem key={wbs.code} value={wbs.code} className="border-b last:border-b-0">
                      <AccordionTrigger className="px-3 py-2 hover:no-underline">
                        <div className="flex items-center gap-2 text-left">
                          <Badge variant="outline" className="text-[10px] font-mono">{wbs.code}</Badge>
                          <span className="text-sm font-medium">{wbs.name}</span>
                          {wbs.activities.length > 0 && (
                            <Badge className="bg-blue-100 text-blue-700 text-[10px]">
                              {wbs.activities.length} activities
                            </Badge>
                          )}
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-3 pb-2">
                        {wbs.description && (
                          <p className="text-xs text-slate-500 mb-2">{wbs.description}</p>
                        )}
                        {wbs.activities.length > 0 && (
                          <div className="space-y-1">
                            {wbs.activities.map((act) => (
                              <div key={act.activity_id} className="flex items-center justify-between py-1 px-2 bg-slate-50 rounded text-xs">
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-slate-400">{act.activity_id}</span>
                                  <span className="text-slate-700">{act.name}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className="text-[10px]">{act.duration_days}d</Badge>
                                  {act.predecessors.length > 0 && (
                                    <span className="text-slate-400 text-[10px]">
                                      ← {act.predecessors.join(', ')}
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>
          )}

          {/* Step 3: Creating */}
          {step === 3 && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-12 w-12 animate-spin text-purple-500 mb-4" />
              <p className="text-lg font-medium text-slate-700">Creating your schedule...</p>
              <p className="text-sm text-slate-500 mt-1">This may take a moment</p>
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-4 flex-shrink-0">
          {step === 1 && (
            <>
              <Button variant="ghost" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleGenerate} disabled={isGenerating} className="gap-2">
                {isGenerating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    Generate Schedule
                  </>
                )}
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button variant="outline" onClick={() => setStep(1)} disabled={isCreating}>
                Back to Edit
              </Button>
              <Button onClick={handleCreate} disabled={isCreating} className="gap-2">
                {isCreating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4" />
                    Create Schedule
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AIScheduleDialog;
